/*
 * Copyright 2026 Sony Corporation
 */
import type { Roadmap, RoadmapActionRequest, RoadmapStep } from '@ips-jit/schema';
import { Box, Button, Chip, CircularProgress, IconButton, Paper, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';

import {
  clock,
  detailOf,
  iconOf,
  progressOf,
  remainingOf,
  shownSteps,
  STAGES,
  statusOf,
  type StepIcon,
} from '../services/roadmap';

import { ChevronIcon } from './icons';

const COLOR: Record<Exclude<StepIcon, 'active'>, string> = {
  done: 'success.main',
  pending: 'text.disabled',
  failed: 'error.main',
  skipped: 'text.disabled',
  removed: 'text.disabled',
};

const SYMBOL: Record<Exclude<StepIcon, 'active'>, string> = {
  done: '✓',
  pending: '',
  failed: '!',
  skipped: '–',
  removed: '–',
};

const LABEL: Record<StepIcon, string> = {
  done: 'done',
  active: 'in progress',
  pending: 'planned',
  failed: 'failed',
  skipped: 'skipped',
  removed: 'removed',
};

const StateIcon = ({ icon }: { icon: StepIcon }) =>
  icon === 'active' ? (
    <CircularProgress size={18} thickness={5} aria-label={LABEL.active} />
  ) : (
    <Box
      aria-label={LABEL[icon]}
      role="img"
      sx={{
        width: 18,
        height: 18,
        borderRadius: '50%',
        flexShrink: 0,
        display: 'grid',
        placeItems: 'center',
        fontSize: 12,
        lineHeight: 1,
        border: 2,
        borderColor: COLOR[icon],
        ...(icon === 'done' || icon === 'failed' ? { bgcolor: COLOR[icon], color: 'common.black' } : {}),
        ...(icon === 'skipped' || icon === 'removed' ? { color: COLOR[icon] } : {}),
      }}
    >
      {SYMBOL[icon]}
    </Box>
  );

/** Where a running step is: the stages before it done, its own in progress, the rest to come. */
const Stages = ({ step }: { step: RoadmapStep }) => {
  const at = STAGES.findIndex((s) => s.id === (step.stage ?? 'writing'));
  return (
    <Typography variant="caption" color="text.secondary" component="p" aria-label="Stage">
      {STAGES.map((stage, i) => (
        <Box
          component="span"
          key={stage.id}
          sx={{
            color: i === at ? 'info.light' : i < at ? 'success.light' : 'text.disabled',
            fontWeight: i === at ? 700 : 400,
          }}
        >
          {i > 0 ? ' → ' : ''}
          {stage.label}
          {i < at ? ' ✓' : ''}
        </Box>
      ))}
    </Typography>
  );
};

export interface RoadmapPanelProps {
  roadmap: Roadmap | null;
  /** The newest thing the assistant said it was doing, shown under the running step. */
  step?: string;
  onAction: (action: RoadmapActionRequest) => Promise<void>;
}

/**
 * The steps the app is built in and where each one stands, updated as the steps run and as the user's messages revise
 * them. It replaces reading the chat to know how far the app has got.
 */
export const RoadmapPanel = ({ roadmap, step: latest, onAction }: RoadmapPanelProps) => {
  const [open, setOpen] = useState(true);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const running = roadmap?.steps.some((s) => s.state === 'running') ?? false;

  useEffect(() => {
    if (!running) {
      return;
    }
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [running]);

  const act = async (action: RoadmapActionRequest) => {
    setBusy(true);
    try {
      await onAction(action);
    } finally {
      setBusy(false);
    }
  };

  const steps = roadmap ? shownSteps(roadmap) : [];
  const status = roadmap ? statusOf(roadmap) : undefined;
  const left = roadmap && ['running', 'paused'].includes(roadmap.status) ? remainingOf(roadmap, now) : 0;
  const revised = roadmap && roadmap.revision > 1 ? roadmap.steps.filter((s) => s.revised_in === roadmap.revision) : [];

  return (
    <Paper variant="outlined" component="section" aria-label="Roadmap" sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Typography variant="subtitle1" sx={{ flex: 1 }}>
          Roadmap{roadmap ? ` (${progressOf(roadmap)})` : ''}
        </Typography>
        {left > 0 && (
          <Typography variant="caption" color="text.secondary">
            about {clock(left * 1000)} left
          </Typography>
        )}
        {roadmap?.status === 'running' && (
          <Button size="small" disabled={busy} onClick={() => void act({ action: 'pause' })}>
            Pause
          </Button>
        )}
        {roadmap?.status === 'paused' && !roadmap.steps.some((s) => s.state === 'failed') && (
          <Button size="small" disabled={busy} onClick={() => void act({ action: 'resume' })}>
            Resume
          </Button>
        )}
        <IconButton size="small" aria-label={open ? 'Hide roadmap' : 'Show roadmap'} onClick={() => setOpen(!open)}>
          <ChevronIcon direction={open ? 'up' : 'down'} fontSize="small" />
        </IconButton>
      </Stack>
      {open && (
        <>
          {!roadmap && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              Describe your app in the chat. The assistant breaks it into steps you can try on the device one after
              another; they appear here.
            </Typography>
          )}
          {status && (
            <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
              {status}
            </Typography>
          )}
          <Box
            aria-live="polite"
            sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}
          >
            {revised.length > 0
              ? `The roadmap changed: ${revised.map((s) => `${s.change} ${s.title}`).join(', ')}.`
              : ''}
          </Box>
          <Stack component="ol" spacing={1.25} sx={{ listStyle: 'none', p: 0, m: 0, mt: 1.5 }}>
            {steps.map((step, i) => {
              const icon = iconOf(step);
              return (
                <Stack
                  component="li"
                  key={step.step_id}
                  direction="row"
                  spacing={1.25}
                  sx={{ alignItems: 'flex-start' }}
                  aria-current={step.state === 'running' ? 'step' : undefined}
                >
                  <Box sx={{ pt: 0.25 }}>
                    <StateIcon icon={icon} />
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                      <Typography
                        variant="body2"
                        color={
                          icon === 'pending' || icon === 'skipped' || icon === 'removed'
                            ? 'text.secondary'
                            : 'text.primary'
                        }
                        sx={{
                          fontWeight: step.state === 'running' ? 700 : 400,
                          textDecoration: icon === 'removed' ? 'line-through' : undefined,
                          flex: 1,
                        }}
                      >
                        {step.state === 'removed' ? '' : `${i + 1}. `}
                        {step.title}
                      </Typography>
                      {step.change && step.change !== 'removed' && step.revised_in === roadmap?.revision && (
                        <Chip
                          size="small"
                          variant="outlined"
                          color={step.change === 'added' ? 'success' : 'info'}
                          label={step.change === 'added' ? 'Added' : 'Changed'}
                        />
                      )}
                    </Stack>
                    {step.state !== 'removed' && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        component="p"
                        sx={{ overflowWrap: 'anywhere' }}
                      >
                        {step.outcome}
                      </Typography>
                    )}
                    <Typography
                      variant="caption"
                      color={step.state === 'failed' ? 'error.light' : 'text.secondary'}
                      component="p"
                    >
                      {detailOf(step, now)}
                    </Typography>
                    {step.state === 'running' && (
                      <>
                        <Stages step={step} />
                        {latest && (
                          <Typography variant="caption" color="text.disabled" component="p" noWrap>
                            {latest}
                          </Typography>
                        )}
                      </>
                    )}
                    {step.state === 'failed' && (
                      <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={busy}
                          onClick={() => void act({ action: 'retry', step_id: step.step_id })}
                        >
                          Try again
                        </Button>
                        <Button
                          size="small"
                          disabled={busy}
                          onClick={() => void act({ action: 'skip', step_id: step.step_id })}
                        >
                          Skip
                        </Button>
                      </Stack>
                    )}
                  </Box>
                </Stack>
              );
            })}
          </Stack>
        </>
      )}
    </Paper>
  );
};
